import type { FirebaseApp } from 'firebase/app'
import type { Analytics } from 'firebase/analytics'

import { env } from '@/lib/env'

import {
  currentFlowSnapshot,
  flowAbandoned,
  getCrashContext,
} from './AnalyticsService'

/**
 * Web crash reporter — fires Firebase Analytics `app_exception` events for
 * uncaught errors, unhandled promise rejections, and React render failures.
 * Chosen over Sentry per the phase-0 plan decision: zero new deps (reuses
 * the already-installed `firebase` package) at the cost of no session
 * replay / no breadcrumbs / no source-map upload out of the box.
 *
 * Every fired event carries the current flow context from
 * `AnalyticsService.getCrashContext()` — so a crash mid-payment shows up
 * in the Firebase console tagged with `flow: "tier_activate"`,
 * `stage: "sheet_opened"`, `stripe_pi_id: pi_xxx`, etc. That's what closes
 * the "attribution" gap this plan flagged as critical.
 *
 * Also owns the ABANDONMENT hook: `visibilitychange` (backgrounding) and
 * `beforeunload` (tab close) fire `{flow}_abandoned` if a payment or
 * onboarding flow is `_started` without a terminal event. Best-effort —
 * we don't await the send because the browser may cut the tab before it
 * completes; we accept the occasional lost event for the funnel-drop-off
 * signal.
 */

// ── Firebase init (mirrors AnalyticsService's guarded init) ───────────────

let app: FirebaseApp | null = null
let analytics: Analytics | null = null

function hasConfig(): boolean {
  try {
    const e = env()
    return !!(
      e.VITE_FIREBASE_API_KEY &&
      e.VITE_FIREBASE_AUTH_DOMAIN &&
      e.VITE_FIREBASE_PROJECT_ID &&
      e.VITE_FIREBASE_APP_ID &&
      e.VITE_FIREBASE_MEASUREMENT_ID
    )
  } catch {
    return false
  }
}

async function ensureAnalytics(): Promise<Analytics | null> {
  if (analytics) return analytics
  if (!hasConfig()) return null
  try {
    // Imported on demand. A static import here put the Firebase chunk in the
    // entry graph via installGlobalCrashHandlers(), so every visitor to a
    // marketing page downloaded it at high priority to support a crash
    // reporter that most sessions never fire.
    //
    // NOTE: this duplicates AnalyticsService's app init, env read and
    // isSupported gate. Worth collapsing into a single shared instance —
    // this file also still reads through the full-schema env(), the coupling
    // that was removed from AnalyticsService on 2026-08-26.
    const { getApps, initializeApp } = await import('firebase/app')
    const existing = getApps()[0]
    if (!existing) {
      const e = env()
      app = initializeApp({
        apiKey: e.VITE_FIREBASE_API_KEY,
        authDomain: e.VITE_FIREBASE_AUTH_DOMAIN,
        projectId: e.VITE_FIREBASE_PROJECT_ID,
        messagingSenderId: e.VITE_FIREBASE_MESSAGING_SENDER_ID,
        appId: e.VITE_FIREBASE_APP_ID,
        measurementId: e.VITE_FIREBASE_MEASUREMENT_ID,
      })
    } else {
      app = existing
    }
    const { getAnalytics, isSupported } = await import('firebase/analytics')
    if (!(await isSupported())) return null
    analytics = getAnalytics(app)
    return analytics
  } catch {
    return null
  }
}

// ── Report a crash ────────────────────────────────────────────────────────

/**
 * Fire an `app_exception` event with the current flow context. Stack
 * strings are truncated to 500 chars (Firebase Analytics's per-string
 * limit is ~500 chars per param — anything longer is silently dropped).
 * Full stacks would be nice but the size cap means we take the head,
 * which is where the user-facing crash actually happens anyway.
 */
export function reportCrash(
  error: unknown,
  extra: { source?: string; componentStack?: string } = {},
): void {
  try {
    const ctx = getCrashContext()
    const message =
      error instanceof Error ? error.message : typeof error === 'string' ? error : 'unknown'
    const stack = error instanceof Error ? error.stack ?? '' : ''
    const params: Record<string, string | number | boolean> = {
      client: 'web',
      // `fatal: false` because from the user's perspective we recovered
      // (the boundary shows a fallback UI). Global handlers set true.
      fatal: extra.source === 'boundary' ? false : true,
      source: extra.source ?? 'unknown',
      message: truncate(message, 500),
      stack: truncate(stack, 500),
    }
    if (extra.componentStack) {
      params.component_stack = truncate(extra.componentStack, 500)
    }
    // Fold in the current flow context so a crash mid-payment carries the
    // Stripe PI id + tier id + squadron id + session id.
    if (ctx.flow) params.flow = ctx.flow
    if (ctx.stage) params.stage = ctx.stage
    if (ctx.sessionId) params.session_id = ctx.sessionId
    if (ctx.squadronId) params.squadron_id = ctx.squadronId
    if (ctx.stripePiId) params.stripe_pi_id = ctx.stripePiId
    if (ctx.tierId) params.tier_id = ctx.tierId
    if (ctx.amountPence != null) params.amount_pence = ctx.amountPence
    if (ctx.userId) params.user_id = ctx.userId

    // Fire and forget — do NOT block error handling on analytics init.
    void ensureAnalytics().then(async (a) => {
      if (!a) return
      try {
        const { logEvent } = await import('firebase/analytics')
        logEvent(a, 'app_exception', params)
      } catch {
        // ignore
      }
    })
  } catch {
    // A crash reporter that itself throws would be a debug loop; swallow.
  }
}

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) : s
}

// ── Global handlers ───────────────────────────────────────────────────────

let installed = false

/**
 * Install `window.onerror` + `unhandledrejection` handlers so any crash
 * not caught by a React error boundary still reaches the reporter.
 * Idempotent — calling twice does nothing on the second call.
 */
export function installGlobalCrashHandlers(): void {
  if (installed) return
  if (typeof window === 'undefined') return
  installed = true

  window.addEventListener('error', (event) => {
    reportCrash(event.error ?? event.message ?? 'unknown', { source: 'window.onerror' })
  })
  window.addEventListener('unhandledrejection', (event) => {
    reportCrash(event.reason ?? 'unhandled rejection', { source: 'unhandledrejection' })
  })

  // Abandonment: when the tab backgrounds or the user closes, fire an
  // `_abandoned` event if a flow is currently started but not terminal.
  // Best-effort — we do NOT await because the browser may not give us
  // the async slot; we care about the *volume* of drop-offs, not the
  // per-event delivery guarantee.
  const maybeFireAbandonment = () => {
    const snap = currentFlowSnapshot()
    if (!snap) return
    // Only bracket payment + onboarding flows — churn from a squadron
    // finance page isn't the same signal.
    if (
      snap.flow.startsWith('squadron_create') ||
      snap.flow.startsWith('member_join') ||
      snap.flow.startsWith('tier_activate') ||
      snap.flow.startsWith('tier_switch') ||
      snap.flow.startsWith('fee_change_accept') ||
      snap.flow.startsWith('tournament_join') ||
      snap.flow.startsWith('prize_claim') ||
      snap.flow === 'onboarding'
    ) {
      flowAbandoned(snap.flow, snap.stage)
    }
  }
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') maybeFireAbandonment()
  })
  window.addEventListener('pagehide', maybeFireAbandonment)
  window.addEventListener('beforeunload', maybeFireAbandonment)
}
